/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import { FormControl, Typography } from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'

import moment, { Moment } from 'config/moment'
import { MULTI_SELECT_STRINGS } from 'consts/strings'
import { ReactComponent as EditIcon } from 'assets/icons/save.svg'
import { Dialog, UploadButton, snackbar } from 'components'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { Tag } from 'features/Main/Tags/types'
import { Membership } from 'features/Main/Memberships/types'
import { resizeFile, ConfigUtils } from 'utils'
import { PrizeCategory } from 'consts/prize'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'
import CustomTextField from 'components/CustomTextField'
import { TabPanel } from 'components/Dialog/TabPanel'
import {
  TimeRestrictionConfig,
  TimeRestrictionConfig as TimeRestrictionConfigType,
} from 'components/TimeRestrictionConfig'
import { PrizeDateRangeFields } from 'features/Main/Memberships/Prizes/components/PrizeDateRangeFields'

import {
  Coupon,
  UpdateCouponResponse,
  UpdateCouponData,
  DataParams,
  DataResponse,
} from '../../types'
import { fetchPushUserCount, updateCoupon } from '../../api'
import { useStyles } from './styles'

interface ValidationData {
  toteatEnabled: boolean
  tucanCategory?: PrizeCategory
  toteatCategory?: PrizeCategory
}

interface TagOption {
  label: string
  value: number
}

interface MembershipOption {
  label: string
  value: number
}

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'Máximo 100 caracteres',
    },
  ],
  accentDescription: [
    {
      validate: (value: string) => value.length <= 100,
      message: 'Máximo 100 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'Máximo 100 caracteres',
    },
  ],
  description: [
    {
      validate: (value: string) => value.length <= 254,
      message: 'Máximo 254 caracteres',
    },
  ],
  expiryDate: [
    {
      validate: (value: Moment | null) => !!value && value.isSameOrAfter(moment().startOf('day')),
      message: 'La fecha de vencimiento no puede ser anterior a hoy',
    },
  ],
  order: [
    {
      validate: (value: string) => (value && Number(value) > 0) || !value,
      message: 'El orden debe ser mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID de beneficio POS es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface UpdateCouponDialogProps {
  handleClose: () => void
  couponToUpdate: Coupon
  onDone: (newCoupon: Coupon) => void
  tags: Tag[]
  memberships: Membership[]
}

const UpdateCouponDialog: React.FC<UpdateCouponDialogProps> = ({
  handleClose,
  onDone,
  couponToUpdate,
  tags,
  memberships,
}) => {
  const classes = useStyles()

  const [toteatBenefitId, setToteatBenefitId] = useState<string>(
    couponToUpdate.toteatBenefitId || '',
  )
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>(
    couponToUpdate.skus ? couponToUpdate.skus.map(s => s.sku) : [],
  )
  const [storesEnabled, setStoresEnabled] = useState(couponToUpdate.storesEnabled)
  const [tucanEnabled, setTucanEnabled] = useState(couponToUpdate.tucanEnabled)
  const [toteatEnabled, setToteatEnabled] = useState(couponToUpdate.toteatEnabled)
  const [tucanCategory, setTucanCategory] = useState<PrizeCategory | undefined>(
    couponToUpdate.tucanCategory || PrizeCategory.Checkout,
  )
  const [toteatCategory, setToteatCategory] = useState<PrizeCategory | undefined>(
    couponToUpdate.toteatCategory || PrizeCategory.Checkout,
  )
  const [title, setTitle] = useState(couponToUpdate.title)
  const [accentDescription, setAccentDescription] = useState(couponToUpdate.accentDescription)
  const [additionalText, setAdditionalText] = useState(couponToUpdate.additionalText)
  const [description, setDescription] = useState(couponToUpdate.description ?? '')
  const [image, setImage] = useState<File | undefined>(undefined)
  const [imagePreview, setImagePreview] = useState(couponToUpdate.imageUrl)
  const [icon, setIcon] = useState<File | undefined>(undefined)
  const [iconPreview, setIconPreview] = useState(couponToUpdate.iconUrl)
  const [startDate, setStartDate] = useState<Moment | null>(
    couponToUpdate.startDate ? moment(couponToUpdate.startDate) : null,
  )
  const [expiryDate, setExpiryDate] = useState<Moment | null>(moment(couponToUpdate.expiryDate))
  const [order, setOrder] = useState(couponToUpdate.order?.toString() ?? '')
  const [tagsSelected, setTagsSelected] = useState<TagOption[]>(
    couponToUpdate?.tags?.map(t => ({ label: t.title, value: t.id })) || [],
  )
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(couponToUpdate.allTagsAreRequired)
  const [membershipsSelected, setMembershipsSelected] = useState<MembershipOption[]>(
    couponToUpdate?.memberships?.map(m => ({ label: m.title, value: m.id })) || [],
  )
  const [userCount, setUserCount] = useState<number | null>(null)
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [timeRestriction, setTimeRestriction] = useState<TimeRestrictionConfigType>({
    timeRestrictionEnabled: couponToUpdate.timeRestrictionEnabled || false,
    allowedDays: couponToUpdate.allowedDays || [],
    startTime: couponToUpdate.startTime || '08:00',
    endTime: couponToUpdate.endTime || '18:00',
  })

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))
  const membershipsForSelect = memberships.map(el => ({ label: el.title, value: el.id }))

  const handleSetTags = async (e: TagOption[]) => {
    setTagsSelected(e)
  }

  const handleSetMemberships = async (e: MembershipOption[]) => {
    setMembershipsSelected(e)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = async () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  const [requestData, isRequestingData] = useApiCall<DataParams, DataResponse>(fetchPushUserCount)

  const handleFetchPushUserCount = async () => {
    try {
      const tagsSelectedId = tagsSelected.map((tag: { value: number }) => tag.value)

      const data = await requestData({
        tagIds: tagsSelectedId,
        allTagsAreRequired,
      })

      setUserCount(data.count)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo calcular la cantidad de usuarios. Intente de nuevo.')
    }
  }

  useEffect(() => {
    handleFetchPushUserCount()
  }, [tagsSelected, allTagsAreRequired])

  const [updateCouponApi, isLoading] = useApiCall<UpdateCouponData, UpdateCouponResponse>(
    updateCoupon,
  )

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setTitle(e.target.value)
  }

  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setDescription(e.target.value)
  }

  const handleAccentDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAccentDescription(e.target.value)
  }

  const handleAdditionalTextChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAdditionalText(e.target.value)
  }

  const handleStartDateChange = (date: Moment | null) => {
    if (date) {
      setStartDate(date.startOf('day'))
    } else {
      setStartDate(null)
    }
  }

  const handleExpiryDateChange = (date: Moment | null) => {
    if (date) {
      setExpiryDate(date.endOf('day'))
    } else {
      setExpiryDate(null)
    }
  }

  const handleImageChange = async (file: File) => {
    setImagePreview(URL.createObjectURL(file))
    const newImage = await resizeFile(file, 600, 600)
    setImage(newImage)
  }

  const handleIconChange = async (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    const newIcon = await resizeFile(file, 192, 192)
    setIcon(newIcon)
  }

  const handleOrderChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setOrder(e.target.value)
  }

  const getRequiredFields = () => {
    const requiredFields = [
      'title',
      'accentDescription',
      'description',
      'image',
      'icon',
      'expiryDate',
      'additionalText',
    ]

    if (toteatEnabled) {
      if (toteatCategory === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (toteatCategory === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    accentDescription,
    description,
    expiryDate,
    order,
    additionalText,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    tucanCategory,
    toteatCategory,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading || hasPartnerErrors

  const onUpdateCoupon = async () => {
    const coupon = {
      title,
      accentDescription,
      description,
      image,
      icon,
      expiryDate: expiryDate?.toISOString(),
      ...(startDate ? { startDate: startDate.toISOString() } : {}),
      order,
      additionalText,
      tagsSelected,
      membershipsSelected,
      allTagsAreRequired,
      tucanCategory,
      toteatCategory,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
      timeRestriction,
    }
    const payload = {
      id: couponToUpdate.id,
      payload: coupon,
    }
    try {
      const { coupon: updatedCoupon } = await updateCouponApi(payload)
      onDone(updatedCoupon)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo editar la promo. Intente de nuevo.')
    }
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  const hasAnyIntegration = ConfigUtils.hasAnyIntegrationEnabled()
  const tabs = hasAnyIntegration
    ? ['Descripción', 'Filtros', 'Horarios', 'Integración']
    : ['Descripción', 'Filtros', 'Horarios']

  return (
    <Dialog
      title="Editar promoción"
      isOpen
      isEdit
      showTabsActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled, startIcon: <EditIcon /> }}
      onAccept={onUpdateCoupon}
      tabs={tabs}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
      showUserCount
      isRequestingData={isRequestingData}
      userCount={userCount!}
      style={{
        minWidth: 600,
        minHeight: 640,
        borderRadius: 15,
      }}
      contentStyle={{
        borderTop: 'none',
        paddingTop: 5,
      }}
    >
      <Grid>
        {/* Tab 0 Descripcion */}
        <TabPanel value={tabIndex} index={0} id="description">
          <Grid container>
            <Grid item xs={12}>
              <CustomTextField
                label="Título promo"
                value={accentDescription}
                fullWidth
                onChange={handleAccentDescriptionChange}
                disabled={isLoading}
                required
                maxLength={100}
                error={errors.accentDescription.hasError}
                helperText={errors.accentDescription.message || rules.accentDescription[0].message}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                label="Título secundario promo"
                value={description}
                fullWidth
                onChange={handleDescriptionChange}
                disabled={isLoading}
                multiline
                maxLength={254}
                error={errors.description.hasError}
                helperText={errors.description.message || rules.description[0].message}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                label="Título wallet"
                value={title}
                fullWidth
                onChange={handleTitleChange}
                disabled={isLoading}
                required
                maxLength={100}
                error={errors.title.hasError}
                helperText={errors.title.message || rules.title[0].message}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                label="Título secundario wallet"
                value={additionalText}
                fullWidth
                onChange={handleAdditionalTextChange}
                disabled={isLoading}
                required
                maxLength={100}
                error={errors.additionalText.hasError}
                helperText={errors.additionalText.message || rules.additionalText[0].message}
              />
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PrizeDateRangeFields
                  startDate={startDate}
                  expiration={expiryDate}
                  onStartDateChange={handleStartDateChange}
                  onExpirationChange={handleExpiryDateChange}
                  disabled={isLoading}
                  requiredExpiration
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  value={order}
                  onChange={handleOrderChange}
                  label="Orden"
                  type="tel"
                  fullWidth
                  error={errors.order.hasError}
                  helperText={errors.order.message}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={classes.iconUploadContainer}>
                <UploadButton
                  id="coupon-icon-upload-button"
                  accept=".jpg, .jpeg, .png"
                  label="Subir ícono *"
                  onChange={handleIconChange}
                />
                <Grid className={classes.iconUploadPreviewContainer}>
                  <img src={iconPreview} alt="" className={classes.iconPreview} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container className={classes.imageUploadContainer}>
                <UploadButton
                  id="coupon-image-upload-button"
                  accept=".jpg, .jpeg, .png"
                  label="Subir imagen *"
                  onChange={handleImageChange}
                />
                <Grid className={classes.imageUploadPreviewContainer}>
                  <img src={imagePreview} alt="" className={classes.imagePreview} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 1 Filtros */}
        <TabPanel value={tabIndex} index={1} id="filters">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography className={classes.dialogSubTitle}>Niveles</Typography>
              <FormControl className={classes.multiselect}>
                <MultiSelect
                  options={membershipsForSelect}
                  value={membershipsSelected}
                  onChange={handleSetMemberships}
                  labelledBy="Seleccionar Niveles"
                  hasSelectAll={false}
                  className={classes.outlinedMultiSelect}
                  overrideStrings={MULTI_SELECT_STRINGS}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.dialogSubTitle}>Tags</Typography>
              <FormControl className={classes.multiselect}>
                <MultiSelect
                  options={tagsForSelect}
                  value={tagsSelected}
                  onChange={handleSetTags}
                  labelledBy="Seleccionar Tags"
                  hasSelectAll={false}
                  className={classes.outlinedMultiSelect}
                  overrideStrings={MULTI_SELECT_STRINGS}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={handleAllTagsAreRequiredChange}
                    checked={allTagsAreRequired}
                  />
                }
                label="Sólo usuarios con todos los tags seleccionados"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2 Horarios */}
        <TabPanel value={tabIndex} index={2} id="time-restriction">
          <TimeRestrictionConfig
            config={timeRestriction}
            onChange={setTimeRestriction}
            disabled={isLoading}
            hideTimeSelectors={process.env.REACT_APP_ECARD_ENABLED === 'true'}
          />
        </TabPanel>

        {/* Tab 3 Integración */}
        {hasAnyIntegration && (
          <TabPanel value={tabIndex} index={3} id="integration">
            <Grid container>
              <PartnerIntegration
                hideTitle
                storesEnabled={storesEnabled}
                setStoresEnabled={setStoresEnabled}
                tucanEnabled={tucanEnabled}
                setTucanEnabled={setTucanEnabled}
                toteatEnabled={toteatEnabled}
                setToteatEnabled={setToteatEnabled}
                tucanCategory={tucanCategory}
                setTucanCategory={setTucanCategory}
                toteatCategory={toteatCategory}
                setToteatCategory={setToteatCategory}
                currentSku={currentSku}
                setCurrentSku={setCurrentSku}
                skusList={skusList}
                setSkusList={setSkusList}
                toteatBenefitId={toteatBenefitId}
                setToteatBenefitId={setToteatBenefitId}
                errors={errors}
                isLoading={isLoading}
                onValidationChange={handlePartnerValidationChange}
              />
            </Grid>
          </TabPanel>
        )}
      </Grid>
    </Dialog>
  )
}

export { UpdateCouponDialog }
